import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { apiUrl } from "../apiUrl";

// 1. Función para obtener el catálogo (thunk)
export const fetchVacunasCatalog = createAsyncThunk(
  "vaccineOrder/fetchVacunasCatalog",
  async () => {
    const response = await axios.get(`${apiUrl}/api/v1/vacunas`);
    return response.data;
  }
);

// 2. Funciones para órdenes de vacunación
export const getVaccineOrdersByVisita = createAsyncThunk(
  "vaccineOrder/getVaccineOrdersByVisita",
  async (visitaId) => {
    const response = await axios.get(
      `${apiUrl}/api/v1/vaccine-orders/visita/${visitaId}`
    );
    return response.data;
  }
);

export const getVaccineOrders = createAsyncThunk(
  "vaccineOrder/getVaccineOrders",
  async () => {
    const response = await axios.get(`${apiUrl}/api/v1/vaccine-orders`);
    return response.data;
  }
);

export const getVaccineOrderById = createAsyncThunk(
  "vaccineOrder/getVaccineOrderById",
  async (orderId) => {
    const response = await axios.get(
      `${apiUrl}/api/v1/vaccine-orders/${orderId}`
    );
    return response.data;
  }
);

export const createVaccineOrder = createAsyncThunk(
  "vaccineOrder/createVaccineOrder",
  async (orderData) => {
    const response = await axios.post(`${apiUrl}/api/v1/vaccine-orders`, {
      visita_id: orderData.visitaId,
      paciente_id: orderData.pacienteId,
      vacuna: orderData.vacuna,
      tipo_dosis: orderData.tipoDosis,
      fecha_aplicacion: orderData.fechaAplicacion,
      observaciones: orderData.observaciones || null,
    });
    return response.data;
  }
);

export const updateVaccineOrder = createAsyncThunk(
  "vaccineOrder/updateVaccineOrder",
  async ({ orderId, orderData }) => {
    const response = await axios.put(
      `${apiUrl}/api/v1/vaccine-orders/${orderId}`,
      orderData
    );
    return response.data;
  }
);

// Eliminar orden individual - Modificado para manejar mejor el estado
export const deleteVaccineOrder = createAsyncThunk(
  "vaccineOrder/deleteVaccineOrder",
  async (orderId, { rejectWithValue }) => {
    try {
      await axios.delete(`${apiUrl}/api/v1/vaccine-orders/${orderId}`);
      return orderId;
    } catch (error) {
      return rejectWithValue({
        message: error.response?.data?.message || error.message,
        id: orderId,
      });
    }
  }
);

// Eliminar órdenes por visita_id - Modificado para manejar mejor el estado
export const deleteVaccineOrdersByVisita = createAsyncThunk(
  "vaccineOrder/deleteVaccineOrdersByVisita",
  async (visitaId, { rejectWithValue }) => {
    try {
      const response = await axios.delete(
        `${apiUrl}/api/v1/vaccine-orders/visita/${visitaId}`
      );
      return {
        visitaId,
        count: response.data.count,
      };
    } catch (error) {
      return rejectWithValue({
        message: error.response?.data?.message || error.message,
        visitaId,
      });
    }
  }
);

const vaccineOrderSlice = createSlice({
  name: "vaccineOrder",
  initialState: {
    catalog: [],
    orders: [],
    loading: false,
    error: null,
    currentOrder: null,
    deleteStatus: "idle", // Nuevo estado para manejar el estado de eliminación
  },
  reducers: {
    setCurrentOrder: (state, action) => {
      state.currentOrder = action.payload;
    },
    clearCurrentOrder: (state) => {
      state.currentOrder = null;
    },
    resetVaccineOrdersState: (state) => {
      state.orders = [];
      state.currentOrder = null;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchVacunasCatalog.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchVacunasCatalog.fulfilled, (state, action) => {
        state.loading = false;
        state.catalog = action.payload;
      })
      .addCase(fetchVacunasCatalog.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      // Get All Orders
      .addCase(getVaccineOrders.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getVaccineOrders.fulfilled, (state, action) => {
        state.loading = false;
        state.orders = action.payload;
      })
      .addCase(getVaccineOrders.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })

      // Get Orders by Visita
      .addCase(getVaccineOrdersByVisita.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getVaccineOrdersByVisita.fulfilled, (state, action) => {
        state.loading = false;
        state.orders = action.payload;
      })
      .addCase(getVaccineOrdersByVisita.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })

      // Get Order by ID
      .addCase(getVaccineOrderById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getVaccineOrderById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentOrder = action.payload;
      })
      .addCase(getVaccineOrderById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })

      // Create Order
      .addCase(createVaccineOrder.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createVaccineOrder.fulfilled, (state, action) => {
        state.loading = false;
        state.orders.push(action.payload);
      })
      .addCase(createVaccineOrder.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })

      // Update Order
      .addCase(updateVaccineOrder.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateVaccineOrder.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.orders.findIndex(
          (order) => order.id === action.payload.id
        );
        if (index !== -1) {
          state.orders[index] = action.payload;
        }
        if (state.currentOrder?.id === action.payload.id) {
          state.currentOrder = action.payload;
        }
      })
      .addCase(updateVaccineOrder.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })

      // Delete Order
      /* .addCase(deleteVaccineOrder.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteVaccineOrder.fulfilled, (state, action) => {
        state.loading = false;
        state.orders = state.orders.filter(
          (order) => order.id !== action.payload
        );
        if (state.currentOrder?.id === action.payload) {
          state.currentOrder = null;
        }
      })
      .addCase(deleteVaccineOrder.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      }) */

      // Eliminar orden individual
      .addCase(deleteVaccineOrder.pending, (state) => {
        state.deleteStatus = "loading";
        state.error = null;
      })
      .addCase(deleteVaccineOrder.fulfilled, (state, action) => {
        state.deleteStatus = "succeeded";
        state.orders = state.orders.filter(
          (order) => order.id !== action.payload
        );
        if (state.currentOrder?.id === action.payload) {
          state.currentOrder = null;
        }
      })
      .addCase(deleteVaccineOrder.rejected, (state, action) => {
        state.deleteStatus = "failed";
        state.error = action.payload?.message || "Error al eliminar la vacuna";
      })

      // Eliminar órdenes por visita_id
      .addCase(deleteVaccineOrdersByVisita.pending, (state) => {
        state.deleteStatus = "loading";
        state.error = null;
      })
      .addCase(deleteVaccineOrdersByVisita.fulfilled, (state, action) => {
        state.deleteStatus = "succeeded";
        const { visitaId } = action.payload;
        state.orders = state.orders.filter(
          (order) => order.visita_id !== visitaId
        );
      })
      .addCase(deleteVaccineOrdersByVisita.rejected, (state, action) => {
        state.deleteStatus = "failed";
        state.error =
          action.payload?.message || "Error al eliminar las vacunas";
      });
  },
});

// 3. Selectores con nombres únicos
export const selectVacunasCatalog = (state) => state.vaccineOrder.catalog;
export const selectVaccineOrders = (state) => state.vaccineOrder.orders;
export const selectCurrentOrder = (state) => state.vaccineOrder.currentOrder;
export const selectVaccineOrderLoading = (state) => state.vaccineOrder.loading;
export const selectVaccineOrderError = (state) => state.vaccineOrder.error;
export const selectVaccineOrderByIdFromState = (id) => (state) =>
  state.vaccineOrder.orders.find((order) => order.id === id);
export const selectDeleteStatus = (state) => state.vaccineOrder.deleteStatus;

// Actions
export const { setCurrentOrder, clearCurrentOrder, resetVaccineOrdersState } =
  vaccineOrderSlice.actions;
export const deleteVaccineOrderById = (id) => async (dispatch) => {
  try {
    await dispatch(deleteVaccineOrder(id)).unwrap();
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export default vaccineOrderSlice.reducer;
